import { colors } from "@areahub/theme";
import { bbox as getBounds } from "@turf/turf";
import { getInitialViewState, useMultiFeatureServices } from "@ui/maps/common";
import * as Case from "change-case";
import { useCallback, useState } from "react";
import { LngLatBoundsLike, MapLayerMouseEvent } from "react-map-gl";
import {
  getFeatureUrl,
  NRIDataCategory,
  NRIDataCategoryPrefix,
} from "../helpers";
import { nriLegends } from "../legends";
import { NRICensusMapPopupProps } from "../nri-census-popup";

const getKeyOfCategory = (category: NRIDataCategory) => {
  return Object.keys(NRIDataCategory).find(
    (key) => NRIDataCategory[key as keyof typeof NRIDataCategory] === category,
  );
};

export interface UseNRICensusLayer {
  tract_id: string;
  category: NRIDataCategory;
  bbox: LngLatBoundsLike;
  latitude: number;
  longitude: number;

  initialPopoverState?: Omit<NRICensusMapPopupProps, "setPopover">;
}

export function useNRICensusLayer({
  category,
  tract_id,
  latitude,
  longitude,
  bbox,
  initialPopoverState,
}: UseNRICensusLayer) {
  const url = getFeatureUrl(category);
  const [popover, setPopover] = useState<Omit<
    NRICensusMapPopupProps,
    "setPopover"
  > | null>(initialPopoverState || null);
  const { stops, props: legendRows } =
    nriLegends[category as keyof typeof nriLegends];
  const service = useMultiFeatureServices([
    {
      url,
      layer: {
        type: "fill",
        id: `${category}-${tract_id}`,
        source: `${category}-${tract_id}`,
        paint: {
          "fill-outline-color": colors.blueGray[900],
          "fill-opacity": 1,
          "fill-color": [
            "interpolate",
            ["linear"],
            [
              "number",
              [
                "get",
                NRIDataCategoryPrefix[
                  getKeyOfCategory(
                    category,
                  ) as keyof typeof NRIDataCategoryPrefix
                ] + "_AFREQ",
              ],
              -9999,
            ],
            ...stops,
          ],
        },
      },
      arcgisOptions: {
        where: `TRACTFIPS = '${tract_id}'`,
      },
    },
    {
      url,
      layer: {
        type: "fill",
        id: `${category}`,
        source: category,
        paint: {
          "fill-outline-color": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            colors.blueGray[800],
            colors.blueGray[300],
          ],
          "fill-opacity": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            0.8,
            0.5,
          ],
          "fill-color": [
            "interpolate",
            ["linear"],
            [
              "number",
              [
                "get",
                NRIDataCategoryPrefix[
                  getKeyOfCategory(
                    category,
                  ) as keyof typeof NRIDataCategoryPrefix
                ] + "_AFREQ",
              ],
              -9999,
            ],
            ...stops,
          ],
        },
      },
    },
  ]);
  const initialViewState = getInitialViewState({
    latitude: latitude,
    longitude: longitude,
  });

  const resetViewState = () =>
    service.ref.current.fitBounds(bbox, {
      padding: 50,
      duration: 800,
    });

  const onLoad = useCallback(() => {
    service.onLoad();
    resetViewState();
  }, [service]);

  const onMouseMove = useCallback(
    (event: MapLayerMouseEvent) => {
      const feature = event?.features?.[0];
      if (popover?.id) {
        service.ref.current.setFeatureState(
          { source: category, id: popover?.id },
          { hover: false },
        );
      }
      if (feature?.properties && feature?.geometry && feature?.id) {
        setPopover({
          id: feature?.id,
          tractfips: feature?.properties?.TRACTFIPS,
          county: feature?.properties?.COUNTY,
          state: feature?.properties?.STATE,
          stat: {
            name: Case.headerCase(category) + " Annual Frequency",
            value:
              feature?.properties?.[
                NRIDataCategoryPrefix[
                  getKeyOfCategory(
                    category,
                  ) as keyof typeof NRIDataCategoryPrefix
                ] + "_AFREQ"
              ],
          },
        });
        service.ref.current.setFeatureState(
          { source: category, id: feature?.id },
          { hover: true },
        );
      }
    },
    [popover],
  );

  const onClick = useCallback(
    (event: MapLayerMouseEvent) => {
      onMouseMove(event);
      const feature = event?.features?.[0];
      if (feature?.properties && feature?.geometry) {
        // calculate the bounding box of the feature
        const [minLng, minLat, maxLng, maxLat] = getBounds(feature);
        service.ref.current.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          { padding: 50, duration: 500 },
        );
        // set popover to feature data
        onMouseMove(event);
      }
    },
    [popover],
  );
  return {
    ref: service.ref,
    onLoad,
    onClick,
    onMouseMove,
    interactiveLayerIds: [category],
    initialViewState,
    popover,
    setPopover,
    resetViewState,
    legendRows,
  };
}
